import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';

interface Feature {
  id: string;
  nameHu: string;
  nameEn: string;
  icon: string;
  category: string;
  order: number;
  active: boolean;
}

const FeatureManager: React.FC = () => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingFeature, setDeletingFeature] = useState<Feature | null>(null);
  const [formData, setFormData] = useState<Partial<Feature>>({
    nameHu: '',
    nameEn: '',
    icon: '',
    category: 'comfort',
    order: 0,
    active: true
  });

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      const q = query(collection(db, 'vehicleFeatures'), orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Feature[];
      setFeatures(data);
    } catch (error) {
      console.error('Error fetching features:', error);
      toast.error('Hiba a jellemzők betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        await updateDoc(doc(db, 'vehicleFeatures', editingId), {
          ...formData,
          updatedAt: new Date()
        });
        toast.success('Jellemző frissítve');
      } else {
        await addDoc(collection(db, 'vehicleFeatures'), {
          ...formData,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        toast.success('Jellemző létrehozva');
      }
      
      setFormData({
        nameHu: '',
        nameEn: '',
        icon: '',
        category: 'comfort',
        order: 0,
        active: true
      });
      setEditingId(null);
      fetchFeatures();
    } catch (error) {
      console.error('Error saving feature:', error);
      toast.error('Hiba a mentés során');
    }
  };

  const handleEdit = (feature: Feature) => {
    setFormData(feature);
    setEditingId(feature.id);
  };

  const handleDeleteClick = (feature: Feature) => {
    setDeletingFeature(feature);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingFeature) return;
    
    try {
      await deleteDoc(doc(db, 'vehicleFeatures', deletingFeature.id));
      toast.success('Jellemző törölve');
      fetchFeatures();
    } catch (error) {
      console.error('Error deleting feature:', error);
      toast.error('Hiba a törlés során');
    } finally {
      setDeletingFeature(null);
    }
  };

  const toggleActive = async (id: string, currentActive: boolean) => {
    try {
      await updateDoc(doc(db, 'vehicleFeatures', id), {
        active: !currentActive,
        updatedAt: new Date()
      });
      toast.success(currentActive ? 'Jellemző inaktiválva' : 'Jellemző aktiválva');
      fetchFeatures();
    } catch (error) {
      console.error('Error toggling feature:', error);
      toast.error('Hiba a művelet során');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Betöltés...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Form */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {editingId ? 'Jellemző szerkesztése' : 'Új jellemző'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Név (Magyar) *
              </label>
              <input
                type="text"
                required
                value={formData.nameHu}
                onChange={(e) => setFormData({ ...formData, nameHu: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Név (Angol) *
              </label>
              <input
                type="text"
                required
                value={formData.nameEn}
                onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Ikon (Emoji) *
              </label>
              <input
                type="text"
                required
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                placeholder="❄️"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Kategória *
              </label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              >
                <option value="comfort">Comfort (Kényelem)</option>
                <option value="technology">Technology (Technológia)</option>
                <option value="safety">Safety (Biztonság)</option>
                <option value="space">Space (Tér)</option>
                <option value="exterior">Exterior (Külső)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sorrend *
              </label>
              <input
                type="number"
                required
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
            >
              {editingId ? 'Frissítés' : 'Létrehozás'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setFormData({
                    nameHu: '',
                    nameEn: '',
                    icon: '',
                    category: 'comfort',
                    order: 0,
                    active: true
                  });
                }}
                className="bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-white px-4 py-2 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500"
              >
                Mégse
              </button>
            )}
          </div>
        </form>
      </div>

      {/* List */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Sorrend
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Ikon
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Név (HU / EN)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Kategória
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Státusz
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Műveletek
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {features.map((feature) => (
              <tr key={feature.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {feature.order}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-2xl">
                  {feature.icon}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                  <div>{feature.nameHu}</div>
                  <div className="text-gray-500 dark:text-gray-400">{feature.nameEn}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {feature.category}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => toggleActive(feature.id, feature.active)}
                    className={`px-2 py-1 text-xs rounded-full ${
                      feature.active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {feature.active ? 'Aktív' : 'Inaktív'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleEdit(feature)}
                    className="text-primary-600 hover:text-primary-900 dark:text-primary-400 mr-3"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(feature)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={!!deletingFeature}
        onClose={() => setDeletingFeature(null)}
        onConfirm={handleDeleteConfirm}
        title="Jellemző törlése"
        deleteMessage={`Biztosan törölni szeretnéd a(z) "${deletingFeature?.nameHu}" jellemzőt?`}
        deleteWarning="Ez a művelet nem vonható vissza. A jellemző véglegesen törlődik."
        danger={true}
      />
    </div>
  );
};

export default FeatureManager;
